import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  Grid
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useCheckout } from '../../contexts/CheckoutContext';
import { green } from '@mui/material/colors';

/**
 * ConfirmationStep Component
 * Final step in the checkout process that displays confirmation of successful payment submission
 */
const ConfirmationStep = () => {
  // Access checkout context
  const {
    customerInfo,
    paymentMethod,
    setCurrentStep,
    resetCheckout,
    calculateAmount,
    getCurrency
  } = useCheckout();

  // Format payment method for display
  const getFormattedPaymentMethod = () => {
    switch (paymentMethod) {
      case 'creditCard':
        return 'Credit Card';
      case 'bankTransfer':
        return 'Bank Transfer';
      case 'interac':
        return 'Interac E-Transfer';
      case 'wireTransfer':
        return 'International Wire Transfer';
      default:
        return paymentMethod;
    }
  };

  // Get formatted payment amount
  const getFormattedAmount = () => {
    const { country, province, state } = customerInfo;
    const amount = calculateAmount(country, province || state);
    
    if (country === 'canada') {
      // For Canadian provinces, display CAD with tax included
      const formattedAmount = new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'CAD' 
      }).format(amount);
      
      return `${formattedAmount} (Tax Included)`;
    } else {
      // For USA and Other, display both CAD and USD
      const cadAmount = new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'CAD' 
      }).format(amount);
      
      // Calculate approximate USD amount (using approximate exchange rate)
      const usdAmount = new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'USD' 
      }).format(amount * 0.73); // Using approximate CAD to USD conversion
      
      return (
        <>
          {cadAmount} (Tax Included)<br />
          <span style={{ fontSize: '0.9em', color: '#666' }}>≈ {usdAmount} USD</span>
        </>
      );
    }
  };

  // Get location display text
  const getLocationText = () => {
    const { country, province, state } = customerInfo;
    
    if (country === 'canada' && province) {
      // Find province name from value
      const provinceName = province.replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      return `${provinceName}, Canada`;
    } else if (country === 'usa' && state) {
      // Find state name from value
      const stateName = state.replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      return `${stateName}, United States`;
    } else if (country === 'other') {
      return 'International';
    }
    
    return country;
  };

  // Handle back button
  const handleBack = () => {
    if (paymentMethod === 'bankTransfer' || paymentMethod === 'wireTransfer') {
      setCurrentStep(2);
    } else if (paymentMethod === 'interac') {
      setCurrentStep(2);
    } else {
      setCurrentStep(1);
    }
  };

  // Handle start new checkout
  const handleNewCheckout = () => {
    resetCheckout();
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <CheckCircleIcon 
            sx={{ fontSize: 80, color: green[500], mb: 2 }} 
          />
          <Typography variant="h4" component="h1" gutterBottom>
            Thank You!
          </Typography>
          <Typography variant="h6" gutterBottom>
            Your payment information has been processed successfully.
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            You will receive a confirmation email shortly with all the details about your Thailand Real Estate Diligence Trip.
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            A receipt will be sent to you once the arrival of funds has been confirmed.
          </Typography>
          
          {customerInfo.reference && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0, 128, 0, 0.1)', borderRadius: 1, display: 'inline-block' }}>
              <Typography variant="h6" gutterBottom>
                Your Reference Number:
              </Typography>
              <Typography variant="h5" fontWeight="bold" fontFamily="monospace">
                {customerInfo.reference}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please save this reference number for your records.
              </Typography>
            </Box>
          )}
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h6" component="h2" gutterBottom>
          Order Summary
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <List disablePadding>
              <ListItem disableGutters>
                <ListItemText 
                  primary="Payment Method" 
                  secondary={getFormattedPaymentMethod()} 
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText 
                  primary="Payment Amount" 
                  secondary={getFormattedAmount()} 
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText 
                  primary="Location" 
                  secondary={getLocationText()} 
                />
              </ListItem>
            </List>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <List disablePadding>
              <ListItem disableGutters>
                <ListItemText
                  primary="Customer Name"
                  secondary={`${customerInfo.firstName} ${customerInfo.lastName}`}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText 
                  primary="Email" 
                  secondary={customerInfo.email} 
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText 
                  primary="Phone" 
                  secondary={customerInfo.phone} 
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleBack}
          >
            Back to Payment
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNewCheckout}
          >
            Start New Checkout
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ConfirmationStep;